<template>
  <div class="container-fluid py-4">
    <div class="row">
      <!-- Coupon Details Card -->
      <div class="col-lg-8 col-md-12 mx-auto">
        <div class="card mb-4">
          <div class="card-header p-0 position-relative mt-n4 mx-3 z-index-2">
            <div
              class="bg-gradient-info shadow-info border-radius-lg pt-4 pb-3 d-flex justify-content-between align-items-center"
            >
              <h6 class="text-white text-capitalize ps-3">折價券詳情</h6>
              <div class="pe-3">
                <router-link
                  :to="`/marketing/coupons/${coupon.id}/edit`"
                  class="btn btn-sm btn-white text-info mb-0"
                >
                  <i class="material-icons text-sm">edit</i> 編輯
                </router-link>
              </div>
            </div>
          </div>
          <div class="card-body">
            <div class="row">
              <div class="col-md-6">
                <ul class="list-group list-group-flush">
                  <li class="list-group-item px-0">
                    <span class="text-secondary text-xs font-weight-bold"
                      >代碼 (Code)</span
                    >
                    <h5 class="mb-0 text-dark">{{ coupon.code }}</h5>
                  </li>
                  <li class="list-group-item px-0">
                    <span class="text-secondary text-xs font-weight-bold"
                      >折扣內容</span
                    >
                    <p class="mb-0 text-sm">
                      <span class="badge bg-gradient-light text-dark border">
                        {{ coupon.type === "fixed" ? "定額" : "百分比" }}
                      </span>
                      <span class="font-weight-bold ms-2">
                        {{
                          coupon.type === "fixed"
                            ? "$" + coupon.value
                            : coupon.value + "%"
                        }}
                      </span>
                    </p>
                  </li>
                  <li class="list-group-item px-0">
                    <span class="text-secondary text-xs font-weight-bold"
                      >狀態</span
                    >
                    <div>
                      <span
                        class="badge badge-sm"
                        :class="
                          coupon.is_active
                            ? 'bg-gradient-success'
                            : 'bg-gradient-secondary'
                        "
                      >
                        {{ coupon.is_active ? "啟用" : "停用" }}
                      </span>
                    </div>
                  </li>
                </ul>
              </div>
              <div class="col-md-6">
                <ul class="list-group list-group-flush">
                  <li class="list-group-item px-0">
                    <span class="text-secondary text-xs font-weight-bold"
                      >使用限制</span
                    >
                    <p class="mb-0 text-sm">
                      <i class="material-icons text-xs me-1">shopping_cart</i>
                      低消:
                      {{ coupon.min_spend ? "$" + coupon.min_spend : "無限制" }}
                    </p>
                    <p class="mb-0 text-sm mt-1">
                      <i class="material-icons text-xs me-1">repeat</i>
                      次數:
                      {{
                        coupon.usage_limit
                          ? coupon.usage_limit + " 次"
                          : "無限制"
                      }}
                    </p>
                  </li>
                  <li class="list-group-item px-0">
                    <span class="text-secondary text-xs font-weight-bold"
                      >有效期限</span
                    >
                    <p class="mb-0 text-sm">
                      {{ formatDate(coupon.start_at) }} <br />
                      ~ <br />
                      {{ formatDate(coupon.end_at) }}
                    </p>
                  </li>
                </ul>
              </div>
            </div>

            <div class="row mt-4" v-if="coupon.notes">
              <div class="col-12">
                <h6
                  class="text-secondary text-xs font-weight-bold text-uppercase"
                >
                  備註
                </h6>
                <div class="p-3 bg-gray-100 border-radius-lg text-sm">
                  {{ coupon.notes }}
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Stats Cards -->
        <div class="row mb-4">
          <div class="col-md-6">
            <div class="card">
              <div class="card-body text-center">
                <h6
                  class="text-secondary text-uppercase text-xs font-weight-bolder"
                >
                  總使用次數
                </h6>
                <h3 class="font-weight-bolder text-info">
                  {{ coupon.real_usage_count || 0 }}
                </h3>
              </div>
            </div>
          </div>
          <div class="col-md-6">
            <div class="card">
              <div class="card-body text-center">
                <h6
                  class="text-secondary text-uppercase text-xs font-weight-bolder"
                >
                  總折抵金額
                </h6>
                <h3 class="font-weight-bolder text-success">
                  ${{ coupon.total_discount_amount || 0 }}
                </h3>
              </div>
            </div>
          </div>
        </div>

        <!-- Usage History -->
        <div class="card">
          <div class="card-header p-0 position-relative mt-n4 mx-3 z-index-2">
            <div
              class="bg-gradient-secondary shadow-secondary border-radius-lg pt-4 pb-3"
            >
              <h6 class="text-white text-capitalize ps-3">
                使用紀錄 (Usage History)
              </h6>
            </div>
          </div>
          <div class="card-body px-0 pb-2">
            <div class="table-responsive p-0">
              <table class="table align-items-center mb-0">
                <thead>
                  <tr>
                    <th
                      class="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7"
                    >
                      訂單編號
                    </th>
                    <th
                      class="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7 ps-2"
                    >
                      買家
                    </th>
                    <th
                      class="text-center text-uppercase text-secondary text-xxs font-weight-bolder opacity-7"
                    >
                      折扣金額
                    </th>
                    <th
                      class="text-center text-uppercase text-secondary text-xxs font-weight-bolder opacity-7"
                    >
                      訂單總額
                    </th>
                    <th
                      class="text-center text-uppercase text-secondary text-xxs font-weight-bolder opacity-7"
                    >
                      日期
                    </th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="order in orders" :key="order.id">
                    <td class="ps-4">
                      <router-link
                        :to="'/orders/' + order.order_number"
                        class="text-xs font-weight-bold text-info"
                      >
                        {{ order.order_number }}
                      </router-link>
                    </td>
                    <td>
                      <p class="text-xs font-weight-bold mb-0">
                        {{ order.buyer ? order.buyer.name : "Unknown" }}
                      </p>
                      <p class="text-xs text-secondary mb-0">
                        {{ order.buyer_email }}
                      </p>
                    </td>
                    <td class="align-middle text-center">
                      <span class="text-success text-xs font-weight-bold"
                        >-${{ order.discount_amount }}</span
                      >
                    </td>
                    <td class="align-middle text-center">
                      <span class="text-secondary text-xs font-weight-bold"
                        >${{ order.total_amount }}</span
                      >
                    </td>
                    <td class="align-middle text-center">
                      <span class="text-secondary text-xs font-weight-bold">{{
                        new Date(order.created_at).toLocaleDateString()
                      }}</span>
                    </td>
                  </tr>
                  <tr v-if="orders.length === 0">
                    <td
                      colspan="5"
                      class="text-center py-4 text-sm text-secondary"
                    >
                      尚無使用紀錄
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from "@/utils/request";

export default {
  name: "CouponDetail",
  data() {
    return {
      coupon: {},
      orders: [],
    };
  },
  async mounted() {
    const id = this.$route.params.id;
    if (id) {
      await this.fetchCoupon(id);
      await this.fetchOrders(id);
    }
  },
  methods: {
    async fetchCoupon(id) {
      try {
        const response = await axios.get(`/api/admin/coupons/${id}`);
        this.coupon = response.data;
      } catch (error) {
        console.error("Error fetching coupon:", error);
      }
    },
    async fetchOrders(id) {
      try {
        const response = await axios.get(`/api/admin/coupons/${id}/orders`);
        this.orders = response.data;
      } catch (error) {
        console.error("Error fetching orders:", error);
      }
    },
    formatDate(dateStr) {
      if (!dateStr) return "無期限";
      // Format: YYYY/MM/DD HH:mm
      const date = new Date(dateStr);
      return (
        date.toLocaleDateString() +
        " " +
        date.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })
      );
    },
  },
};
</script>
